import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Clock, Tag, Info, Sparkles, Flame, Zap, Star, Crown, Film, Music, Play, Bot, Palette, Scissors, Wand2, Tv, Video } from "lucide-react";
import { Product } from "@/lib/api";
import { ProductDetailDialog } from "./ProductDetailDialog";

interface ProductCardProps {
  product: Product;
}

const categoryConfig: Record<string, { gradient: string; icon: React.ReactNode; shadowColor: string }> = {
  netflix: { 
    gradient: "from-[#E50914] to-[#B81D24]", 
    icon: <Film className="h-4 w-4 sm:h-5 sm:w-5" />,
    shadowColor: "shadow-[0_8px_30px_rgba(229,9,20,0.3)]"
  },
  spotify: { 
    gradient: "from-[#1DB954] to-[#158D3E]", 
    icon: <Music className="h-4 w-4 sm:h-5 sm:w-5" />,
    shadowColor: "shadow-[0_8px_30px_rgba(29,185,84,0.3)]"
  },
  youtube: { 
    gradient: "from-[#FF0000] to-[#CC0000]", 
    icon: <Play className="h-4 w-4 sm:h-5 sm:w-5" />,
    shadowColor: "shadow-[0_8px_30px_rgba(255,0,0,0.3)]"
  },
  chatgpt: { 
    gradient: "from-[#10A37F] to-[#0D8F6F]", 
    icon: <Bot className="h-4 w-4 sm:h-5 sm:w-5" />,
    shadowColor: "shadow-[0_8px_30px_rgba(16,163,127,0.3)]"
  },
  canva: { 
    gradient: "from-[#7B2BFB] to-[#00C4CC]", 
    icon: <Palette className="h-4 w-4 sm:h-5 sm:w-5" />,
    shadowColor: "shadow-[0_8px_30px_rgba(123,43,251,0.3)]"
  },
  capcut: { 
    gradient: "from-[#F72585] to-[#7209B7]", 
    icon: <Scissors className="h-4 w-4 sm:h-5 sm:w-5" />,
    shadowColor: "shadow-[0_8px_30px_rgba(247,37,133,0.3)]"
  },
  disney: { 
    gradient: "from-[#113CCF] to-[#0D2A8A]", 
    icon: <Star className="h-4 w-4 sm:h-5 sm:w-5" />,
    shadowColor: "shadow-[0_8px_30px_rgba(17,60,207,0.3)]"
  },
  gemini: { 
    gradient: "from-[#4285F4] to-[#8B5CF6]", 
    icon: <Wand2 className="h-4 w-4 sm:h-5 sm:w-5" />,
    shadowColor: "shadow-[0_8px_30px_rgba(66,133,244,0.3)]"
  },
  hbo: { 
    gradient: "from-[#5822B4] to-[#3A1078]", 
    icon: <Tv className="h-4 w-4 sm:h-5 sm:w-5" />,
    shadowColor: "shadow-[0_8px_30px_rgba(88,34,180,0.3)]"
  },
  prime: { 
    gradient: "from-[#00A8E1] to-[#146EB4]", 
    icon: <Video className="h-4 w-4 sm:h-5 sm:w-5" />,
    shadowColor: "shadow-[0_8px_30px_rgba(0,168,225,0.3)]"
  },
  viu: { 
    gradient: "from-[#FFB800] to-[#FF8C00]", 
    icon: <Crown className="h-4 w-4 sm:h-5 sm:w-5" />,
    shadowColor: "shadow-[0_8px_30px_rgba(255,184,0,0.3)]"
  },
  default: { 
    gradient: "from-primary to-primary/80", 
    icon: <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />,
    shadowColor: "shadow-lg"
  },
};

export function ProductCard({ product }: ProductCardProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const config = categoryConfig[product.categorySlug] || categoryConfig.default;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Card className={`group overflow-hidden border-0 bg-card/80 backdrop-blur-sm hover:scale-[1.02] transition-all duration-300 ${config.shadowColor} hover:shadow-2xl`}>
      <div className="relative">
        {/* Badges */}
        <div className="absolute top-2 left-2 sm:top-3 sm:left-3 z-10 flex flex-wrap gap-1">
          {product.isBestSeller && (
            <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 text-[8px] sm:text-[10px] font-semibold px-1.5 sm:px-2 py-0.5 shadow-lg">
              <Flame className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
              <span className="hidden sm:inline">Best Seller</span>
              <span className="sm:hidden">Best</span>
            </Badge>
          )}
          {product.isHot && (
            <Badge className="bg-gradient-to-r from-red-700 to-pink-700 text-white border-0 text-[8px] sm:text-[10px] font-semibold px-1.5 sm:px-2 py-0.5 shadow-lg">
              <Zap className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
              Hot
            </Badge>
          )}
          {product.isNew && (
            <Badge className="bg-gradient-to-r from-emerald-700 to-teal-700 text-white border-0 text-[8px] sm:text-[10px] font-semibold px-1.5 sm:px-2 py-0.5 shadow-lg">
              <Sparkles className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
              New
            </Badge>
          )}
          {product.isPromo && (
            <Badge className="bg-gradient-to-r from-violet-700 to-purple-700 text-white border-0 text-[8px] sm:text-[10px] font-semibold px-1.5 sm:px-2 py-0.5 shadow-lg">
              <Tag className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
              Promo
            </Badge>
          )}
        </div>

        {/* Product Image/Icon */}
        <div className={`h-20 sm:h-36 bg-gradient-to-br ${config.gradient} flex items-center justify-center relative overflow-hidden`}>
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{ 
              backgroundImage: 'radial-gradient(circle at 25% 25%, white 2%, transparent 2%), radial-gradient(circle at 75% 75%, white 2%, transparent 2%)',
              backgroundSize: '30px 30px'
            }} />
          </div>
          
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={product.name}
              className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 text-white">
              <div className="p-2.5 sm:p-4 rounded-lg sm:rounded-2xl bg-white/20 backdrop-blur-sm shadow-xl group-hover:scale-110 transition-transform duration-300">
                {config.icon}
              </div>
            </div>
          )}
          
          {/* Gradient overlay */}
          <div className="absolute inset-x-0 bottom-0 h-10 sm:h-16 bg-gradient-to-t from-card to-transparent" />
        </div>
      </div>

      <CardContent className="p-2 sm:p-4 space-y-1.5 sm:space-y-3 -mt-2 sm:-mt-4 relative">
        {/* Category Icon Badge */}
        <div className="flex items-start justify-between gap-1.5 sm:gap-2">
          <div className="flex items-center gap-1.5 sm:gap-3">
            <div className={`w-6 h-6 sm:w-10 sm:h-10 rounded-md sm:rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-lg ring-1 sm:ring-2 ring-background`}>
              <span className="text-white scale-75 sm:scale-100">{config.icon}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-foreground line-clamp-1 text-[11px] sm:text-sm">{product.name}</h3>
              <div className="flex items-center gap-1 text-[9px] sm:text-xs text-muted-foreground mt-0.5">
                <Clock className="h-2 w-2 sm:h-3 sm:w-3" />
                <span>{product.duration} {product.durationUnit}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Price and Type */}
        <div className="flex items-center justify-between pt-0.5 sm:pt-1">
          <Badge variant="secondary" className="bg-muted text-muted-foreground border border-border/50 text-[7px] sm:text-[10px] font-medium px-1 sm:px-2 py-0.5">
            {product.type}
          </Badge>
          <div className="text-right">
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="text-[8px] sm:text-xs text-muted-foreground line-through mr-1">
                {formatPrice(product.originalPrice)}
              </span>
            )}
            <span className="font-bold text-primary text-xs sm:text-lg">{formatPrice(product.price)}</span>
          </div>
        </div>

        {/* Description - hidden on mobile */}
        <p className="hidden sm:block text-xs text-muted-foreground line-clamp-2 leading-relaxed">
          {product.description}
        </p>

        {/* Note - hidden on mobile */}
        {product.note && (
          <div className="hidden sm:flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 p-2.5 rounded-lg border border-border/30">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-primary" />
            <span className="line-clamp-2">{product.note}</span>
          </div>
        )}
      </CardContent>

      <CardFooter className="p-2 sm:p-4 pt-0">
        <Button
          size="sm"
          className={`w-full bg-gradient-to-r ${config.gradient} hover:opacity-90 text-white border-0 font-semibold shadow-lg transition-all duration-300 hover:shadow-xl text-[10px] sm:text-sm h-7 sm:h-9`}
          disabled={product.stock === "outOfStock"}
          onClick={() => setDialogOpen(true)}
        >
          {product.stock === "outOfStock" ? (
            "Stok Habis"
          ) : (
            <>
              <span className="sm:hidden">Detail</span>
              <span className="hidden sm:inline">Lihat Detail</span>
              <Sparkles className="h-2.5 w-2.5 sm:h-4 sm:w-4 ml-1 sm:ml-2" />
            </>
          )}
        </Button>
      </CardFooter>

      <ProductDetailDialog 
        product={product} 
        open={dialogOpen} 
        onOpenChange={setDialogOpen} 
      />
    </Card>
  );
}
